import { exclamationIcon, checkIcon, closeIcon, loadingIcon, infoIcon, clockIcon } from './icons';

// Bid status specific mapping - can be customized for different bid statuses
const bidStatusMap = {
  "error": { icon: 'close', bg: '#4A2825', color: '#F16C5B' },
  "new": { icon: 'info', bg: '#353942', color: '#A3A9B7' },
  "pending insert": { icon: 'clock', bg: '#5C4A2D', color: '#F6C244' },
  "insert accepted": { icon: 'check', bg: '#29462B', color: '#7BE081' },
  "pending amend": { icon: 'clock', bg: '#5C4A2D', color: '#F6C244' },
  "amend accepted": { icon: 'check', bg: '#29462B', color: '#7BE081' },
  "pending cancel": { icon: 'clock', bg: '#5C4A2D', color: '#F6C244' },
  "cancelled": { icon: 'close', bg: '#4A2825', color: '#F16C5B' },
  "traded": { icon: 'check', bg: '#29462B', color: '#7BE081' },
  "expired": { icon: 'exclamation', bg: '#4A2825', color: '#F16C5B' },
  "passed": { icon: 'exclamation', bg: '#4A2825', color: '#F16C5B' },
  "traded away": { icon: 'exclamation', bg: '#4A2825', color: '#F16C5B' },
  "cover": { icon: 'exclamation', bg: '#4A2825', color: '#F16C5B' },
};

export class BidStatusBadgeCellRenderer {
  status: string;
  text: string;
  eGui: HTMLDivElement;

  init(params) {
    let statusValue = params.value;
    if (typeof statusValue === 'object' && statusValue !== null) {
      statusValue = statusValue.status || statusValue.BID_STATUS || '';
    }
    this.status = (statusValue || '').toString().toLowerCase();
    this.text = params.valueFormatted || params.value || '';
    
    // Don't create badge if value is blank
    if (!this.status || !this.text) {
      this.eGui = document.createElement('div');
      return;
    }
    
    this.eGui = document.createElement('div');
    this.eGui.className = 'badge';
    this.updateBadge(params);
  }

  getGui() {
    return this.eGui;
  }

  refresh(params) {
    let statusValue = params.value;
    if (typeof statusValue === 'object' && statusValue !== null) {
      statusValue = statusValue.status || statusValue.BID_STATUS || '';
    }
    this.status = (statusValue || '').toString().toLowerCase();
    this.text = params.valueFormatted || params.value || '';
    
    // Don't create badge if value is blank
    if (!this.status || !this.text) {
      return false;
    }
    
    this.updateBadge(params);
    return true;
  }

  updateBadge(params) {
    const map = bidStatusMap[this.status] || { icon: 'info', bg: '#888', color: '#fff' };
    this.eGui.style.background = map.bg;
    this.eGui.style.color = map.color;
    this.eGui.style.display = 'flex';
    this.eGui.style.alignItems = 'center';
    this.eGui.style.borderRadius = '20px';
    this.eGui.style.padding = '0 15px 0 10px';
    this.eGui.style.height = '28px';
    this.eGui.style.gap = '2px';
    this.eGui.style.justifyContent = 'flex-start';
    this.eGui.innerHTML = `
      <span class="icon">${this.getIcon(map.icon, map.color)}</span>
      <span style="padding-top:3px;">${this.getBidStatusText(this.text, params)}</span>
    `;
  }

  getBidStatusText(text, params) {
    // Custom text handling for bid statuses - display the "to" value, not the enum key
    switch (text.toLowerCase()) {
      case 'error':
        return 'Error';
      case 'new':
        return 'New';
      case 'pending insert':
      case 'pending amend':
      case 'pending cancel':
        return 'Pending';
      case 'insert accepted':
      case 'amend accepted':
        return 'Submitted';
      case 'cancelled':
        return 'Cancelled';
      case 'traded':
        return 'Executed';
      case 'expired':
        return 'Expired';
      case 'passed':
        return 'Passed';
      case 'traded away':
        return 'Traded Away';
      case 'cover':
        return 'Cover';
      default:
        return text;
    }
  }

  getIcon(icon, color) {
    switch (icon) {
      case 'exclamation':
        return exclamationIcon(color);
      case 'check':
        return checkIcon;
      case 'close':
        return closeIcon;
      case 'loading':
        return loadingIcon(color);
      case 'clock':
        return clockIcon(color);
      case 'info':
        return infoIcon;
      default:
        return '';
    }
  }

  // Static formatter used by templates (mirrors BondStatusBadgeCellRenderer)
  static formatStatusBadge(status: string, _dueTime?: string, _firmTime?: string) {
    const statusLower = (status || '').toString().toLowerCase();
    const map = bidStatusMap[statusLower] || { icon: 'info', bg: '#888', color: '#fff' };

    const getText = (text: string) => {
      const lower = (text || '').toLowerCase();
      switch (lower) {
        case 'pending insert':
        case 'pending amend':
        case 'pending cancel':
          return 'Pending';
        case 'insert accepted':
        case 'amend accepted':
          return 'Submitted';
        case 'passed all':
          return 'Passed';
        default:
          return text;
      }
    };

    const iconFor = (icon: string, color: string) => {
      switch (icon) {
        case 'exclamation':
          return exclamationIcon(color);
        case 'check':
          return checkIcon;
        case 'close':
          return closeIcon;
        case 'loading':
          return loadingIcon(color);
        case 'clock':
          return clockIcon(color);
        case 'info':
          return infoIcon;
        default:
          return '';
      }
    };

    return {
      map,
      getText: () => getText(status),
      getFormattedText: () => getText(status),
      getIcon: (icon: string, color: string) => iconFor(icon, color),
    };
  }
}
